import WhitelabelDomainStatus from '@/Enums/Whitelabels/WhitelabelDomainStatus.enum';
import { IWhitelabelFormData } from '@/Interfaces/Whitelabels/WhitelabelFormData.interface';
import StatusChip from '@/components/StatusChip';
import { isDomainSecondLevel, sanitiseDomain } from '@/helpers/domain.helpers';
import { Button } from '@mui/material';
import { memo } from 'react';
import { WHITELABEL_DOMAIN_STATUS_CHIP_PROPS } from './constants';

interface IProps {
  selectedWhitelabel: IWhitelabelFormData;
  isExternal?: boolean;
  onRerunVerification?: () => void;
}

const DomainStatus = memo(({ selectedWhitelabel, isExternal, onRerunVerification }: IProps) => {
  const domainStatus = isExternal ? selectedWhitelabel?.domainExternalStatus : selectedWhitelabel?.domainStatus;
  const domain = isExternal ? selectedWhitelabel?.domainExternal : selectedWhitelabel?.domain;
  const shouldShowStatus = !!domain && !!domainStatus;

  // Detect if this is a second-level domain (e.g. help-grodno.by)
  const sanitisedDomain = sanitiseDomain(domain);
  const isSecondLevelDomain = isDomainSecondLevel(sanitisedDomain);

  return (
    <>
      {shouldShowStatus && (
        <div className='mt-2 flex items-center gap-2'>
          <div className='text-sm flex items-center gap-2'>
            Domain status:
            <StatusChip {...WHITELABEL_DOMAIN_STATUS_CHIP_PROPS[domainStatus]} />
            {sanitisedDomain && isExternal && domainStatus === WhitelabelDomainStatus.Failed && (
              <Button
                onClick={onRerunVerification}
                variant='contained'
                sx={{ padding: '3px 9px', fontWeight: 400, fontSize: '12px' }}
              >
                Rerun Verification
              </Button>
            )}
          </div>
        </div>
      )}

      {isExternal &&
        ![WhitelabelDomainStatus.Pending, WhitelabelDomainStatus.Success, WhitelabelDomainStatus.Failed, null].includes(
          domainStatus,
        ) && (
          <div className='mt-2 flex flex-col gap-2 bg-[#1B432324] p-4 rounded-xl'>
            <p>
              Your domain has been successfully added.{' '}
              <strong>First, add the TXT record below and wait at least 1.5 hours</strong> for verification to complete.
            </p>
            <div className='overflow-x-auto'>
              <table className='w-full text-left border-collapse'>
                <thead>
                  <tr className='bg-[#1B432340]'>
                    <th className='p-2 font-bold'>Type</th>
                    <th className='p-2 font-bold'>Name</th>
                    <th className='p-2 font-bold'>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {/* TXT verification record */}
                  <tr className='border-t border-[#1B432350]'>
                    <td className='p-2'>TXT</td>
                    <td className='p-2'>{sanitisedDomain}.</td>
                    <td className='p-2'>{selectedWhitelabel.domainVerificationRecord}.</td>
                  </tr>

                  {/* CNAME for main domain */}
                  <tr className='border-t border-[#1B432350] text-gray-500'>
                    <td className='p-2'>CNAME</td>
                    <td className='p-2'>{sanitisedDomain}.</td>
                    <td className='p-2'>ghs.googlehosted.com.</td>
                  </tr>

                  {/* Extra CNAME for www if domain is second-level */}
                  {isSecondLevelDomain && (
                    <tr className='border-t border-[#1B432350] text-gray-500'>
                      <td className='p-2'>CNAME</td>
                      <td className='p-2'>www.{sanitisedDomain}.</td>
                      <td className='p-2'>ghs.googlehosted.com.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className='text-red-600 font-semibold'>
              Add CNAME records only after TXT verification completes (wait ~1.5 hours).
            </p>

            <p>
              Changes may take a few hours to propagate. Check your domain status at:{' '}
              <strong>
                <a href={`https://${sanitisedDomain}`} target='_blank' rel='noreferrer'>
                  https://{sanitisedDomain}
                </a>
                {isSecondLevelDomain && (
                  <>
                    {' or '}
                    <a href={`https://www.${sanitisedDomain}`} target='_blank' rel='noreferrer'>
                      https://www.{sanitisedDomain}
                    </a>
                  </>
                )}
              </strong>
            </p>
          </div>
        )}
    </>
  );
});

export default DomainStatus;
